import { createCSVFile, downloadZip } from "../lib/exportCsv.ts";
import { CsvEncoding } from "../lib/config.ts";
import { createZip } from "../lib/createZipFile.ts";
import { getCurrentDateTime } from "../lib/getCurrentDateTime.ts";
import { CancelError, CancelManager } from "../lib/cancelManager.ts";
import { ExportProgressStatus } from "../modules/parts/ExportProgressModal.tsx";
import { getPaginatedData } from "../lib/api/getPaginatedData.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import {
  IOrganization,
  IOrganizationField,
  IGroup,
} from "../models/organization.ts";
import {
  transformFromOrganizationToCsv,
  buildOrganizationCsvHeaders,
  buildGroupMap,
} from "../lib/transform/transformFromOrganizationToCsv.ts";

interface ExportOrganizationParams {
  token: string;
  createdStartDate: string;
  createdEndDate: string;
  updatedStartDate: string;
  updatedEndDate: string;
  orgNameKeyword: string;
  onUpdateTotalCount: (count: number) => void;
  onUpdateDownloadedCount: (count: number) => void;
  onUpdateFetchedCount: (count: number) => void;
  onUpdateInfo: (status: ExportProgressStatus, message: string) => void;
  csvEncoding: CsvEncoding;
}

export const exportOrganizationData = async (
  params: ExportOrganizationParams,
) => {
  const {
    token,
    createdStartDate,
    createdEndDate,
    updatedStartDate,
    updatedEndDate,
    orgNameKeyword,
    onUpdateTotalCount,
    onUpdateDownloadedCount,
    onUpdateFetchedCount,
    onUpdateInfo,
    csvEncoding,
  } = params;

  const cancelManager = CancelManager.getInstance();
  cancelManager.reset();

  try {
    // 1. クエリ構築
    const queryParts: string[] = ["type:organization"];
    if (orgNameKeyword) queryParts.push(orgNameKeyword);
    if (createdStartDate) queryParts.push(`created>=${createdStartDate}`);
    if (createdEndDate) queryParts.push(`created<=${createdEndDate}`);
    if (updatedStartDate) queryParts.push(`updated>=${updatedStartDate}`);
    if (updatedEndDate) queryParts.push(`updated<=${updatedEndDate}`);
    const query = queryParts.join(" ");

    // 2. マスタデータ取得（並列実行）
    onUpdateInfo("Export", "マスタデータを取得しています。(1/3)");
    const baseUrl = `https://${import.meta.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com`;

    const [orgFieldsRaw, groupsRaw] = await Promise.all([
      zendeskHttpRequest<{ organization_fields: IOrganizationField[] }>(
        "organization_fields.json",
        token,
        "GET",
      ),
      zendeskHttpRequest<{ groups: IGroup[] }>("groups.json", token, "GET"),
    ]);

    const activeFields = orgFieldsRaw.organization_fields.filter(
      (f) => f.active,
    );
    const groupMap = buildGroupMap(groupsRaw.groups);

    cancelManager.checkCancelled();

    // 3. 組織データ取得（ページネーション）
    onUpdateInfo("Export", "組織データを取得しています。(2/3)");
    const apiUrl = `${baseUrl}/api/v2/search/export.json?filter[type]=organization&query=${encodeURIComponent(query)}`;
    const organizations = await getPaginatedData<IOrganization>(
      apiUrl,
      token,
      baseUrl,
      "results",
      onUpdateFetchedCount,
    );

    if (organizations.length === 0) {
      onUpdateInfo(
        "Success",
        "エクスポート対象の組織データはありませんでした。",
      );
      return;
    }

    cancelManager.checkCancelled();

    // 4. アプリ側フィルタリング（組織名キーワード）
    let filtered = organizations;
    if (orgNameKeyword) {
      const keyword = orgNameKeyword.toLowerCase();
      filtered = organizations.filter((org) =>
        org.name?.toLowerCase().includes(keyword),
      );
    }

    if (filtered.length === 0) {
      onUpdateInfo(
        "Success",
        "フィルター条件に一致する組織データはありませんでした。",
      );
      return;
    }

    onUpdateTotalCount(filtered.length);

    // 5. CSV変換
    onUpdateInfo("Export", "CSVファイルを作成しています。(3/3)");
    const csvData = filtered.map((org, index) => {
      onUpdateDownloadedCount(index + 1);
      return transformFromOrganizationToCsv(org, groupMap, activeFields);
    });

    const csvHeaders = buildOrganizationCsvHeaders(activeFields);

    // 6. ダウンロード
    onUpdateInfo("Success", "");
    const csvFile = createCSVFile(csvData, csvHeaders, csvEncoding);
    const zip = await createZip(csvFile, "export.csv", []);
    downloadZip(zip, `organizations_${getCurrentDateTime()}`);
  } catch (e) {
    console.error(e);
    if (e instanceof CancelError) {
      onUpdateInfo("Cancel", "");
    } else {
      onUpdateInfo("Error", `${e}`);
    }
  }
};
